import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ContentCopy as ContentCopyIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import useEntertainment from '../../hooks/useEntertainment';
import usePrintserverConfig from '../../hooks/usePrintserverConfig';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const trimSafe = v => (typeof v === 'string' ? v.trim() : '');

const copyToClipboard = async text => {
  const value = typeof text === 'string' ? text : '';
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (_) {
    return false;
  }
};

const parseEnvLike = text => {
  const out = {};
  const raw = typeof text === 'string' ? text : '';
  const lines = raw.split(/\r?\n/);
  for (const lineRaw of lines) {
    const line = String(lineRaw || '').trim();
    if (!line) continue;
    if (line.startsWith('#')) continue;
    const idx = line.indexOf('=');
    if (idx <= 0) continue;
    const key = line.slice(0, idx).trim();
    if (!/^[A-Z][A-Z0-9_]*$/.test(key)) continue;
    let value = line.slice(idx + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    out[key] = value;
  }
  return out;
};

const parseEcosystemLike = text => {
  const out = {};
  const raw = typeof text === 'string' ? text : '';

  // Match both `KEY: value` and `KEY=value` patterns for uppercase env keys.
  const re = /(^|[^A-Z0-9_])([A-Z][A-Z0-9_]{1,40})\s*[:=]\s*(['"]?)([^'"\r\n,}]+)\3/gm;
  let m;
  while ((m = re.exec(raw))) {
    const key = m[2];
    const value = String(m[4] || '').trim();
    if (!key || !value) continue;
    out[key] = value;
  }
  return out;
};

const parseReferenceConfig = text => {
  // Ecosystem parsing is intentionally permissive and also matches env-like inputs.
  // We still merge both to be safe (env takes precedence).
  const eco = parseEcosystemLike(text);
  const env = parseEnvLike(text);
  return { ...eco, ...env };
};

const shouldApplyImportedValue = value => {
  const v = String(value ?? '').trim();
  if (!v) return false;
  const lower = v.toLowerCase();
  if (v.startsWith('<') && v.endsWith('>')) return false;
  if (lower.includes('process.env')) return false;
  if (lower === 'undefined' || lower === 'null') return false;
  return true;
};

const buildEnvText = kv => {
  const keys = Object.keys(kv || {}).sort();
  return keys.map(k => `${k}=${kv[k]}`).join('\n');
};

const coerceEcosystemLiteral = value => {
  const v = String(value ?? '').trim();
  if (!v) return '""';
  if (/^(true|false)$/i.test(v)) return v.toLowerCase();
  if (/^-?\d+(\.\d+)?$/.test(v)) return v;
  return JSON.stringify(v);
};

const buildEcosystemText = ({ appName, script, envProduction }) => {
  const envKeys = Object.keys(envProduction || {}).sort();
  const envLines = envKeys.map(k => `        ${k}: ${coerceEcosystemLiteral(envProduction[k])},`);
  return [
    `module.exports = {`,
    `  apps: [`,
    `    {`,
    `      name: ${JSON.stringify(appName || 'app')},`,
    `      script: ${JSON.stringify(script || 'app.js')},`,
    `      env_production: {`,
    ...envLines,
    `      },`,
    `    },`,
    `  ],`,
    `};`,
  ].join('\n');
};

export default function PrintserverConfig() {
  const theme = useTheme();
  const { getEntertainments } = useEntertainment();
  const { createPrintserverConfig, rotateServiceAccountSecret, isLoading } = usePrintserverConfig();

  const [entertainments, setEntertainments] = useState([]);
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState('');
  const [result, setResult] = useState(null);
  const [loadingEntertainments, setLoadingEntertainments] = useState(true);

  const [printserverOutputMode, setPrintserverOutputMode] = useState('env'); // env | ecosystem
  const [locationserverOutputMode, setLocationserverOutputMode] = useState('env'); // env | ecosystem

  // Use refs to avoid controlled input onChange handlers for text inputs.
  const printerNameRef = useRef(null);
  const backendUrlRef = useRef(null);
  const mqttUrlPrintserverRef = useRef(null);
  const mqttUrlLocationserverRef = useRef(null);
  const mqttUsernameRef = useRef(null);
  const mqttPasswordRef = useRef(null);
  const locationIdRef = useRef(null);
  const portRef = useRef(null);
  const debugLevelRef = useRef(null);
  const printCardRef = useRef(null);
  const cupsCancelPendingOnRebindRef = useRef(null);
  const printerCupsNameRef = useRef(null);
  const printerUsbPortRef = useRef(null);
  const printerCupsAutofixRef = useRef(null);

  const emergencyTotpSecretRef = useRef(null);
  const onlineMqttUrlRef = useRef(null);
  const onlineMqttUsernameRef = useRef(null);
  const onlineMqttPasswordRef = useRef(null);
  const localPrintEnabledRef = useRef(null);

  const printserverReferenceRef = useRef(null);
  const locationserverReferenceRef = useRef(null);
  const lastAutoLocationIdRef = useRef('');
  const lastAutoPrinterNameRef = useRef('');
  const lastAutoMqttUrlLocationserverRef = useRef('');

  const entertainmentOptions = useMemo(() => {
    const list = Array.isArray(entertainments) ? entertainments : [];
    return list
      .map(e => ({
        id: e?._id || '',
        label: e?.longName || e?.entertainmentName || e?._id || '',
        invoiceCode: e?.branch?.invoiceCode || '',
      }))
      .filter(x => x.id && x.label)
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  }, [entertainments]);

  const selectedLabel = useMemo(() => {
    return entertainmentOptions.find(o => o.id === selectedEntertainmentId)?.label || '';
  }, [entertainmentOptions, selectedEntertainmentId]);

  const selectedInvoiceCode = useMemo(() => {
    const raw = entertainmentOptions.find(o => o.id === selectedEntertainmentId)?.invoiceCode;
    return trimSafe(raw) ? String(raw).trim().toUpperCase() : '';
  }, [entertainmentOptions, selectedEntertainmentId]);

  const applyAutoLocationId = invoiceCode => {
    const code = trimSafe(invoiceCode) ? String(invoiceCode).trim().toUpperCase() : '';
    if (!code) return;
    if (!locationIdRef?.current) return;

    const current = String(locationIdRef.current.value || '').trim();
    const lastAuto = String(lastAutoLocationIdRef.current || '').trim();

    // Only auto-fill when empty or still equal to the previous auto-filled value.
    if (!current || current === lastAuto) {
      locationIdRef.current.value = code;
      lastAutoLocationIdRef.current = code;
    }
  };

  const applyAutoPrinterName = invoiceCode => {
    const code = trimSafe(invoiceCode) ? String(invoiceCode).trim().toUpperCase() : '';
    if (!code) return;
    if (!printerNameRef?.current) return;

    const derived = `Member-Card-Printer-${code}`;
    const current = String(printerNameRef.current.value || '').trim();
    const lastAuto = String(lastAutoPrinterNameRef.current || '').trim();

    // Only auto-fill when empty or still equal to the previous auto-filled value.
    if (!current || current === lastAuto) {
      printerNameRef.current.value = derived;
      lastAutoPrinterNameRef.current = derived;
    }
  };

  const applyAutoMqttUrlLocationserver = () => {
    if (!mqttUrlLocationserverRef?.current) return;
    const derived = 'mqtt://localhost:1883';
    const current = String(mqttUrlLocationserverRef.current.value || '').trim();
    const lastAuto = String(lastAutoMqttUrlLocationserverRef.current || '').trim();
    if (!current || current === lastAuto) {
      mqttUrlLocationserverRef.current.value = derived;
      lastAutoMqttUrlLocationserverRef.current = derived;
    }
  };

  const loadEntertainments = async () => {
    try {
      setLoadingEntertainments(true);
      const data = await getEntertainments(true);
      setEntertainments(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Fehler beim Laden der Entertainments');
    } finally {
      setLoadingEntertainments(false);
    }
  };

  useEffect(() => {
    loadEntertainments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyAutoLocationId(selectedInvoiceCode);
    applyAutoPrinterName(selectedInvoiceCode);
    applyAutoMqttUrlLocationserver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvoiceCode]);

  const handleGenerate = async () => {
    if (!selectedEntertainmentId) {
      toast.error('Bitte ein Entertainment auswählen');
      return;
    }

    const readRef = r => (r?.current && typeof r.current.value === 'string' ? r.current.value : '');
    const printerName = readRef(printerNameRef).trim();
    const backendUrl = readRef(backendUrlRef).trim();
    const mqttUrlPrintserver = readRef(mqttUrlPrintserverRef).trim();
    const mqttUrlLocationserver = readRef(mqttUrlLocationserverRef).trim();
    const mqttUsername = readRef(mqttUsernameRef).trim();
    const mqttPassword = readRef(mqttPasswordRef);
    const locationId = readRef(locationIdRef).trim();
    const portRaw = readRef(portRef).trim();
    const port = portRaw && !Number.isNaN(Number(portRaw)) ? Number(portRaw) : undefined;
    const debugLevelRaw = readRef(debugLevelRef).trim();
    const debugLevel =
      debugLevelRaw && !Number.isNaN(Number(debugLevelRaw)) ? Number(debugLevelRaw) : undefined;
    const printCard = readRef(printCardRef).trim();
    const cupsCancelPendingOnRebind = readRef(cupsCancelPendingOnRebindRef).trim();
    const printerCupsName = readRef(printerCupsNameRef).trim();
    const printerUsbPort = readRef(printerUsbPortRef).trim();
    const printerCupsAutofix = readRef(printerCupsAutofixRef).trim();

    const emergencyTotpSecret = readRef(emergencyTotpSecretRef).trim();
    const onlineMqttUrl = readRef(onlineMqttUrlRef).trim();
    const onlineMqttUsername = readRef(onlineMqttUsernameRef).trim();
    const onlineMqttPassword = readRef(onlineMqttPasswordRef);
    const localPrintEnabled = readRef(localPrintEnabledRef).trim();

    try {
      const data = await createPrintserverConfig({
        entertainmentId: selectedEntertainmentId,
        printerName: printerName || undefined,
        backendUrl: backendUrl || undefined,
        mqttUrlPrintserver: mqttUrlPrintserver || undefined,
        mqttUrlLocationserver: mqttUrlLocationserver || undefined,
        mqttUsername: mqttUsername || undefined,
        mqttPassword: mqttPassword || undefined,
        locationId: locationId || undefined,
        port: port || undefined,
        debugLevel: debugLevel ?? undefined,
        printCard: printCard || undefined,
        cupsCancelPendingOnRebind: cupsCancelPendingOnRebind || undefined,
        printerCupsName: printerCupsName || undefined,
        printerUsbPort: printerUsbPort || undefined,
        printerCupsAutofix: printerCupsAutofix || undefined,
        emergencyTotpSecret: emergencyTotpSecret || undefined,
        onlineMqttUrl: onlineMqttUrl || undefined,
        onlineMqttUsername: onlineMqttUsername || undefined,
        onlineMqttPassword: onlineMqttPassword || undefined,
        localPrintEnabled: localPrintEnabled || undefined,
      });
      setResult(data);
      toast.success('Konfiguration erzeugt');
    } catch (_) {}
  };

  const handleRotate = async () => {
    const id = result?.serviceAccount?.serviceAccountId;
    if (!id) return;
    try {
      const data = await rotateServiceAccountSecret(id);
      const next = {
        ...result,
        serviceAccount: {
          ...result?.serviceAccount,
          apiKeyPublicId: data?.apiKeyPublicId || result?.serviceAccount?.apiKeyPublicId,
          apiKeySecret: data?.apiKeySecret || null,
        },
      };
      setResult(next);
      toast.success('Secret rotiert');
    } catch (_) {}
  };

  const copyBlock = async (text, label) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(`${label} kopiert`);
    else toast.error('Kopieren fehlgeschlagen');
  };

  const derivedInvoiceCode = useMemo(() => {
    const code = result?.entertainment?.branch?.invoiceCode;
    return trimSafe(code) ? String(code).trim().toUpperCase() : '';
  }, [result?.entertainment?.branch?.invoiceCode]);

  const outputBlocks = useMemo(() => {
    const lsEnv = result?.copyPasteBlocks?.locationserverEnv || '';
    const psEnv = result?.copyPasteBlocks?.printserverEnv || '';

    const lsKv = parseEnvLike(lsEnv);
    const psKv = parseEnvLike(psEnv);

    const locationserverEcosystem = buildEcosystemText({
      appName: derivedInvoiceCode ? `locationserver_${derivedInvoiceCode}` : 'locationserver',
      script: 'app.js',
      envProduction: lsKv,
    });
    const printserverEcosystem = buildEcosystemText({
      appName: derivedInvoiceCode ? `membercard_printer_${derivedInvoiceCode}` : 'membercard_printer',
      script: 'app.js',
      envProduction: psKv,
    });

    return {
      locationserver: {
        env: lsEnv,
        ecosystem: locationserverEcosystem,
      },
      printserver: {
        env: psEnv,
        ecosystem: printserverEcosystem,
      },
    };
  }, [result, derivedInvoiceCode]);

  return (
    <Box sx={{ p: 2, ...viewRootTextPrimarySx }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant='h6'>Printserver/Locationserver konfigurieren</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Dieser Wizard erzeugt alle Werte für Printserver + Locationserver (ServiceAccount, PrinterStatus,
          Copy/Paste .env Blöcke). Der Druckername basiert standardmäßig auf dem Branch-InvoiceCode.
        </Typography>

        {loadingEntertainments ? (
          <Box
            sx={{
              ...viewRootTextPrimarySx,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 2 }} variant='outlined'>
              <Typography variant='subtitle1' sx={{ mb: 1 }}>
                Referenz übernehmen
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Du kannst eine bestehende Konfiguration (alte Anlage) hier reinkopieren. Das Eingabeformat
                wird automatisch erkannt (egal ob .env oder ecosystem.config.js).
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Box>
                  <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                    <Typography variant='subtitle2'>Printserver Referenz</Typography>
                  </Box>
                  <TextField
                    placeholder='Hier altes printserver .env oder ecosystem.config.js einfügen'
                    multiline
                    minRows={4}
                    maxRows={10}
                    fullWidth
                    inputRef={printserverReferenceRef}
                    onPaste={e => {
                      const pasted = e?.clipboardData?.getData?.('text') || '';
                      if (pasted) {
                        // Parse directly from clipboard to avoid relying on onChange.
                        const kv = parseReferenceConfig(pasted);
                        const applyIfPresent = (ref, value) => {
                          if (!ref?.current) return;
                          if (!shouldApplyImportedValue(value)) return;
                          ref.current.value = String(value);
                        };
                        applyIfPresent(mqttUrlPrintserverRef, kv.MQTT_URL);
                        applyIfPresent(mqttUsernameRef, kv.MQTT_USERNAME);
                        applyIfPresent(mqttPasswordRef, kv.MQTT_PASSWORD);
                        applyIfPresent(portRef, kv.PORT);
                        applyIfPresent(debugLevelRef, kv.DEBUG_LEVEL);
                        applyIfPresent(printCardRef, kv.PRINT_CARD);
                        applyIfPresent(cupsCancelPendingOnRebindRef, kv.CUPS_CANCEL_PENDING_ON_REBIND);
                        applyIfPresent(printerCupsNameRef, kv.PRINTER_CUPS_NAME);
                        applyIfPresent(printerUsbPortRef, kv.PRINTER_USB_PORT);
                        applyIfPresent(printerCupsAutofixRef, kv.PRINTER_CUPS_AUTOFIX);
                        applyAutoPrinterName(selectedInvoiceCode);
                        toast.success('Printserver Referenz übernommen');
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                    <Typography variant='subtitle2'>Locationserver Referenz</Typography>
                  </Box>
                  <TextField
                    placeholder='Hier altes locationserver .env oder ecosystem.config.js einfügen'
                    multiline
                    minRows={4}
                    maxRows={10}
                    fullWidth
                    inputRef={locationserverReferenceRef}
                    onPaste={e => {
                      const pasted = e?.clipboardData?.getData?.('text') || '';
                      if (pasted) {
                        const kv = parseReferenceConfig(pasted);
                        const applyIfPresent = (ref, value) => {
                          if (!ref?.current) return;
                          if (!shouldApplyImportedValue(value)) return;
                          ref.current.value = String(value);
                        };
                        applyIfPresent(backendUrlRef, kv.BACKEND_URL);
                        // Locationserver always connects to the local broker.
                        applyAutoMqttUrlLocationserver();
                        applyIfPresent(mqttUsernameRef, kv.MQTT_USERNAME);
                        applyIfPresent(mqttPasswordRef, kv.MQTT_PASSWORD);
                        if (
                          locationIdRef?.current &&
                          shouldApplyImportedValue(kv.LOCATION_ID) &&
                          String(locationIdRef.current.value || '').trim() === String(kv.LOCATION_ID || '').trim()
                        ) {
                          // Keep LOCATION_ID derived from the selected Branch.invoiceCode (new location), not from pasted configs.
                          locationIdRef.current.value = '';
                        }
                        applyAutoLocationId(selectedInvoiceCode);
                        applyIfPresent(emergencyTotpSecretRef, kv.EMERGENCY_TOTP_SECRET);
                        applyIfPresent(onlineMqttUrlRef, kv.ONLINE_MQTT_URL);
                        applyIfPresent(onlineMqttUsernameRef, kv.ONLINE_MQTT_USERNAME);
                        applyIfPresent(onlineMqttPasswordRef, kv.ONLINE_MQTT_PASSWORD);
                        applyIfPresent(localPrintEnabledRef, kv.LOCAL_PRINT_ENABLED);
                        toast.success('Locationserver Referenz übernommen');
                      }
                    }}
                  />
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                gap: 2,
                alignItems: 'center',
                mb: 2,
              }}
            >
              <TextField
                select
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: {
                    PaperProps: {
                      sx: { maxHeight: 360 },
                    },
                  },
                }}
                label='Entertainment'
                value={selectedEntertainmentId}
                onChange={e => setSelectedEntertainmentId(e.target.value)}
                size='small'
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value=''>Bitte auswählen...</MenuItem>
                {entertainmentOptions.map(o => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant='outlined'
                startIcon={<RefreshIcon />}
                onClick={loadEntertainments}
                sx={{ height: 40 }}
              >
                Refresh
              </Button>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              <Paper sx={{ p: 2 }} variant='outlined'>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  MQTT Daten (offline / lokaler Broker)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label='Printserver MQTT URL (optional)'
                    placeholder='z.B. mqtt://10.24.7.164:1883'
                    size='small'
                    inputRef={mqttUrlPrintserverRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Locationserver MQTT URL'
                    placeholder='mqtt://localhost:1883'
                    size='small'
                    inputRef={mqttUrlLocationserverRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='MQTT Username (optional)'
                    size='small'
                    inputRef={mqttUsernameRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='MQTT Password (optional)'
                    type='password'
                    size='small'
                    inputRef={mqttPasswordRef}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Paper>

              <Paper sx={{ p: 2 }} variant='outlined'>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  Printserver (funzone-printserver-mqtt)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label='Printer Name (optional override)'
                    placeholder='z.B. Member-Card-Printer-HH'
                    size='small'
                    inputRef={printerNameRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Printserver Port (optional)'
                    placeholder='3002'
                    size='small'
                    inputRef={portRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Print Card (optional)'
                    placeholder='true/false'
                    size='small'
                    inputRef={printCardRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Debug Level (optional)'
                    placeholder='1'
                    size='small'
                    inputRef={debugLevelRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Cancel pending on rebind (optional)'
                    placeholder='true/false'
                    size='small'
                    inputRef={cupsCancelPendingOnRebindRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Printer CUPS Name (optional)'
                    placeholder='EVOLIS_Primacy'
                    size='small'
                    inputRef={printerCupsNameRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Printer USB Port (optional)'
                    placeholder='/dev/usb/lp0'
                    size='small'
                    inputRef={printerUsbPortRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Printer CUPS Autofix (optional)'
                    placeholder='true/false'
                    size='small'
                    inputRef={printerCupsAutofixRef}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Paper>

              <Paper sx={{ p: 2 }} variant='outlined'>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>
                  Locationserver (funzone-locationserver)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label='Backend URL (optional)'
                    placeholder='z.B. https://management-backend.example'
                    size='small'
                    inputRef={backendUrlRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Location ID (optional)'
                    placeholder='z.B. HH'
                    size='small'
                    inputRef={locationIdRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Emergency TOTP Secret (optional)'
                    placeholder='z.B. VMTK...'
                    size='small'
                    inputRef={emergencyTotpSecretRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Online MQTT URL (optional)'
                    placeholder='z.B. mqtts://...:8883'
                    size='small'
                    inputRef={onlineMqttUrlRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Online MQTT Username (optional)'
                    size='small'
                    inputRef={onlineMqttUsernameRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Online MQTT Password (optional)'
                    type='password'
                    size='small'
                    inputRef={onlineMqttPasswordRef}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label='Local print enabled (optional)'
                    placeholder='true/false'
                    size='small'
                    inputRef={localPrintEnabledRef}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Paper>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flex: '1 1 auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Ausgabe Locationserver
                  </Typography>
                  <ToggleButtonGroup
                    size='small'
                    value={locationserverOutputMode}
                    exclusive
                    onChange={(_, v) => v && setLocationserverOutputMode(v)}
                  >
                    <ToggleButton value='env'>.env</ToggleButton>
                    <ToggleButton value='ecosystem'>ecosystem</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Ausgabe Printserver
                  </Typography>
                  <ToggleButtonGroup
                    size='small'
                    value={printserverOutputMode}
                    exclusive
                    onChange={(_, v) => v && setPrintserverOutputMode(v)}
                  >
                    <ToggleButton value='env'>.env</ToggleButton>
                    <ToggleButton value='ecosystem'>ecosystem</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              <Button
                variant='contained'
                onClick={handleGenerate}
                disabled={isLoading || !selectedEntertainmentId}
              >
                {isLoading ? 'Erzeuge...' : 'Konfiguration erzeugen'}
              </Button>
              {result?.serviceAccount?.serviceAccountId && (
                <Button variant='outlined' onClick={handleRotate} disabled={isLoading}>
                  Secret rotieren
                </Button>
              )}
            </Box>
          </>
        )}

        {result && (
          <>
            <Divider sx={{ my: 2 }} />

            <Alert severity='info' sx={{ mb: 2 }}>
              Standort:{' '}
              <Box component='span' sx={{ fontWeight: 600 }}>
                {result?.entertainment?.longName || selectedLabel || '-'}
              </Box>{' '}
              — PrinterName:{' '}
              <Box component='span' sx={{ fontWeight: 600 }}>
                {result?.printer?.printerName || '-'}
              </Box>
            </Alert>

            {result?.serviceAccount?.apiKeySecret && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                Das Secret wird nur einmal angezeigt. Kopiere es jetzt in die .env vom Locationserver.
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography variant='subtitle1'>Locationserver</Typography>
                    <ToggleButtonGroup
                      size='small'
                      value={locationserverOutputMode}
                      exclusive
                      onChange={(_, v) => v && setLocationserverOutputMode(v)}
                    >
                      <ToggleButton value='env'>.env</ToggleButton>
                      <ToggleButton value='ecosystem'>ecosystem</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<ContentCopyIcon />}
                    onClick={() =>
                      copyBlock(outputBlocks.locationserver[locationserverOutputMode] || '', 'Locationserver')
                    }
                  >
                    Copy
                  </Button>
                </Box>
                <Box
                  sx={{
                    fontFamily: 'monospace',
                    p: 1,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {outputBlocks.locationserver[locationserverOutputMode] || ''}
                </Box>
              </Paper>

              <Paper sx={{ p: 2 }}>
                <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography variant='subtitle1'>Printserver</Typography>
                    <ToggleButtonGroup
                      size='small'
                      value={printserverOutputMode}
                      exclusive
                      onChange={(_, v) => v && setPrintserverOutputMode(v)}
                    >
                      <ToggleButton value='env'>.env</ToggleButton>
                      <ToggleButton value='ecosystem'>ecosystem</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<ContentCopyIcon />}
                    onClick={() =>
                      copyBlock(outputBlocks.printserver[printserverOutputMode] || '', 'Printserver')
                    }
                  >
                    Copy
                  </Button>
                </Box>
                <Box
                  sx={{
                    fontFamily: 'monospace',
                    p: 1,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {outputBlocks.printserver[printserverOutputMode] || ''}
                </Box>
              </Paper>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}

